import { Fragment } from 'react';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import {
  clearTwoFactorChallenge,
  verifyTwoFactorLogin,
} from '../../store/features/auth';
import { TwoFactorChallenge } from './TwoFactorChallenge';

/**
 * Global 2FA step for Google One Tap, popup OAuth, and other non-login surfaces.
 * Login page keeps its inline challenge; this modal covers everywhere else.
 */
export function TwoFactorAuthModal() {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const {
    twoFactorPending,
    twoFactorToken,
    twoFactorEmail,
    isLoggingIn,
    error,
  } = useAppSelector((state) => state.auth);

  const onLoginPage = location.pathname.startsWith('/login');
  const isOpen = Boolean(twoFactorPending && twoFactorToken && !onLoginPage);

  const handleSubmit = async (code: string) => {
    if (!twoFactorToken) return;
    await dispatch(verifyTwoFactorLogin({ twoFactorToken, code }));
  };

  const handleBack = () => {
    dispatch(clearTwoFactorChallenge());
  };

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog className="relative z-[250]" onClose={() => undefined}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-[2px]" aria-hidden />
        </TransitionChild>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 translate-y-2 scale-[0.98]"
            enterTo="opacity-100 translate-y-0 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0 scale-100"
            leaveTo="opacity-0 translate-y-2 scale-[0.98]"
          >
            <DialogPanel className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl ring-1 ring-slate-900/5">
              <TwoFactorChallenge
                email={twoFactorEmail ?? undefined}
                onSubmit={handleSubmit}
                onBack={handleBack}
                isSubmitting={isLoggingIn}
                error={error}
              />
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
}
